/*
Temporary Modal serves as a stop gap until 
decision reached on React UI Library: 
https://www.notion.so/landtech/React-UI-Library-9362776376de40c19f37f441068e3afe
*/

import { AnimatePresence, motion } from "framer-motion";
import { PropsWithChildren, ReactNode, useEffect } from "react";
import classNames from "classnames";

export interface TemporaryModalProps {
  isOpen: boolean;
  title: ReactNode;
  titleClass?: string;
  width?: string;
  headerClass?: string;
  handleClose?: () => void;
}

export function TemporaryModal({
  isOpen,
  children,
  title,
  titleClass,
  width,
  headerClass,
  handleClose,
}: PropsWithChildren<TemporaryModalProps>) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // if a user clicks on an autocomplete suggestion from the browsers autocomplete suggestion
      // a keypress gets fired but e.code is undefined
      if (e.code?.toLocaleUpperCase() === "ESCAPE" && handleClose) {
        handleClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className={classNames(
            "fixed",
            "inset-0",
            "bg-[#000]/50",
            "z-99999",
            "h-full",
            "w-full",
            "flex",
            "items-center",
            "justify-center"
          )}
          data-testid="modal-wrapper"
        >
          <button className={classNames("block", "absolute", "inset-0", "cursor-default")} onClick={handleClose} />

          <motion.div
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ opacity: { duration: 0.05 }, duration: 0.1 }}
            className={classNames(
              width ?? "w-1/3 min-w-[500px]",
              "relative",
              "bg-white",
              "rounded-lg",
              "overflow-hidden"
            )}
          >
            <div
              className={classNames(
                "border-b",
                "border-neutral-300",
                "bg-neutral-100",
                headerClass ?? "py-4 px-5",
                "flex",
                "items-center",
                "justify-between"
              )}
              data-testid="modal-title"
            >
              <h1 className={classNames(titleClass ?? "text-lg font-normal")}>{title}</h1>
              <i
                className={classNames(
                  "icon-lt-close-line",
                  "text-2xl",
                  "text-text-base-primary",
                  "hover:cursor-pointer",
                  "hover:bg-neutral-200",
                  "hover:rounded-full",
                  "hover:w-8",
                  "hover:text-center",
                  "hover:-mr-1"
                )}
                onClick={handleClose}
              />
            </div>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
